import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, FileText } from "lucide-react";
import PageHeader from "../components/PageHeader";
import Reveal, { EASE } from "../components/Reveal";

const POLICIES = [
  {
    tab: "Booking Terms",
    items: [
      { q: "How do I book a trip?", a: "Submit an enquiry through the website, WhatsApp or email. We confirm availability, share a detailed itinerary and hold your seats for 72 hours. Your booking is confirmed once we receive the advance payment (30% of the trip cost) and a signed booking form." },
      { q: "Payments & balance", a: "An advance of 30% confirms your seats. The balance is due 21 days before departure. For bookings made within 21 days of departure, the full amount is payable at the time of booking. All prices are per person on twin-sharing unless stated otherwise." },
      { q: "Travel documents & eligibility", a: "Guests must carry valid government-issued photo ID (and for international trips, a passport valid for at least 6 months beyond the return date, plus any required visas). Guests under 18 must travel with a parent or guardian. It is the traveller's responsibility to meet health, visa and entry requirements." },
      { q: "Itinerary changes & flexibility", a: "We may occasionally adjust itineraries for weather, road conditions, permits or safety. Any substitution will be of equal or higher standard. Significant changes before departure will always be discussed with you first." },
      { q: "Health, fitness & safety", a: "Certain trips (treks, high-altitude routes) require a reasonable fitness level — requirements are listed on each package. We reserve the right to remove a traveller from an activity if their condition endangers themselves or the group; related costs are the traveller's responsibility." },
      { q: "Travel insurance", a: "Travel insurance is mandatory on all our departures and must cover trip cancellation, medical emergencies and evacuation. For high-altitude and adventure itineraries, ensure your policy covers the specific activities involved." },
    ],
  },
  {
    tab: "Cancellation Policy",
    items: [
      { q: "Cancelling by the traveller", a: "Cancellation requests must be sent in writing (email or WhatsApp) to us. The date we receive the written request is treated as the cancellation date, and the following charges apply per person: 30+ days before departure — 15% of trip cost; 29–15 days — 50%; 14–7 days — 75%; fewer than 7 days or no-show — 100%." },
      { q: "Cancelling by Cosmos Routes", a: "In the rare event that we must cancel a departure (e.g., insufficient group size or conditions we consider unsafe), you will receive a full refund or a credit toward another departure, at your choice. We will always aim to give you at least 14 days' notice." },
      { q: "Force majeure", a: "For cancellations caused by events beyond our control (natural events, political disruption, health emergencies), we will recover amounts from our suppliers where possible and offer the remainder as a travel credit valid for 12 months." },
      { q: "Partial cancellations & transfers", a: "If one member of a booking cancels, remaining travellers will be re-invoiced at the applicable twin-sharing rate. Name transfers to another traveller are permitted up to 10 days before departure at a flat admin fee." },
    ],
  },
  {
    tab: "Privacy Policy",
    items: [
      { q: "What we collect", a: "We collect the details you share through enquiry forms, bookings and communications: name, contact details, travel preferences, dietary and health information relevant to your trip, and payment status. We use this only to plan and operate your journey and to keep you informed." },
      { q: "How we use your data", a: "Your information is used to respond to enquiries, process bookings, arrange services with hotels and transport partners, meet legal requirements, and — only with your consent — send occasional trip ideas. We never sell your personal data." },
      { q: "Sharing with third parties", a: "We share only the minimum information necessary with suppliers (hotels, transporters, activity partners) to deliver your trip, and with authorities where legally required. International trips may involve partners outside your country; we require all partners to handle data responsibly." },
      { q: "Cookies & analytics", a: "Our website uses essential cookies and standard analytics to understand how the site is used and improve it. You can control cookies through your browser settings." },
      { q: "Your rights", a: "You may ask us at any time for a copy of the data we hold about you, request corrections, or ask us to delete information we no longer need. Write to contact@cosmosroutes.com and we will respond within 30 days." },
    ],
  },
  {
    tab: "Refund Policy",
    items: [
      { q: "Refund timelines", a: "Approved refunds are processed to your original payment method within 10–14 business days of confirmation. Bank and card networks may take an additional 5–7 business days to reflect the amount." },
      { q: "How refunds are calculated", a: "Refunds follow the cancellation schedule in our Cancellation Policy. Amounts already committed to hotels, permits, internal flights or third-party operators on your behalf cannot be recovered and are therefore non-refundable — we will always share these commitments transparently." },
      { q: "Unutilised services", a: "No refunds are due for services voluntarily unutilised during the trip (missed meals, transfers or activities). If we cancel a listed service mid-trip, the cost of that service will be refunded proportionately." },
      { q: "Travel credits & transfers", a: "Instead of a cash refund, you may choose a travel credit for the full recoverable amount — valid for 12 months on any Cosmos Routes departure, fully transferable to friends and family." },
    ],
  },
];

function PolicyItem({ item, open, onToggle }) {
  return (
    <div className="border-b border-white/10 last:border-0">
      <button
        onClick={onToggle}
        data-testid="policy-accordion-trigger"
        className="flex w-full items-center justify-between gap-6 py-6 text-left"
      >
        <span className="font-display text-base font-bold text-slate-100 md:text-lg">{item.q}</span>
        <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.3, ease: EASE }} className="shrink-0">
          <Plus className={`h-4 w-4 ${open ? "text-aurora" : "text-slate-400"}`} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="overflow-hidden"
          >
            <p className="pb-7 text-sm leading-relaxed text-slate-400 md:text-[15px]">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Policies() {
  const [tab, setTab] = useState(0);
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <div data-testid="page-policies">
      <PageHeader
        testid="policies-header"
        eyebrow="The Fine Print, Made Human"
        title="Booking terms &"
        accent="policies"
        description="Everything about bookings, cancellations, privacy and refunds — written the way we would want to read it."
      />

      <section className="mx-auto max-w-4xl px-6 pb-24 md:pb-32">
        <Reveal>
          <div className="flex flex-wrap gap-2.5" data-testid="policies-tabs">
            {POLICIES.map((p, i) => (
              <button
                key={p.tab}
                data-testid={`policy-tab-${p.tab.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => {
                  setTab(i);
                  setOpenIdx(0);
                }}
                className={`rounded-full border px-5 py-2.5 text-xs font-semibold tracking-wide transition-all duration-300 ${
                  tab === i ? "border-aurora bg-aurora text-ink" : "border-white/15 text-slate-300 hover:border-aurora/50 hover:text-aurora"
                }`}
              >
                {p.tab}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-10 rounded-[2rem] border border-white/10 bg-obsidian/70 px-7 py-3 md:px-10" data-testid="policy-content">
            <div className="flex items-center gap-3 border-b border-white/5 py-5">
              <FileText className="h-4 w-4 text-aurora" />
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                {POLICIES[tab].tab} · Last updated January 2026 · Questions? contact@cosmosroutes.com
              </p>
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={tab}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35, ease: EASE }}
              >
                {POLICIES[tab].items.map((item, i) => (
                  <PolicyItem key={item.q} item={item} open={openIdx === i} onToggle={() => setOpenIdx(openIdx === i ? -1 : i)} />
                ))}
              </motion.div>
            </AnimatePresence>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
